import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ChevronRight, Download, TriangleAlert } from 'lucide-react';
import { API } from '../api';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import Tabs from '../ui/Tabs';
import DataTable from '../ui/DataTable';
import EmptyState from '../ui/EmptyState';
import ErrorState from '../ui/ErrorState';
import Loading from '../ui/Loading';
import EntityLink from '../ui/EntityLink';
import { Facts } from '../ui/EntityHeader';
import { SeverityBadge } from '../ui/Badge';
import { environmentLabel, formatCount, plural } from '../ui/format';
import { EXAMPLES, FILTERS_BY_QUERY, SYNTAX_GROUPS, TIPS } from './queryReference';

const HISTORY_KEY = 'investigation_history';
const MAX_HISTORY = 20;
const ROW_SIZE_OPTIONS = [50, 100, 200];
const DEFAULT_ROWS = 50;

const readHistory = () => {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
  } catch {
    return [];
  }
};
const addToHistory = (query) => {
  const next = [query, ...readHistory().filter((q) => q !== query)].slice(0, MAX_HISTORY);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  return next;
};

const exportToCsv = (rows, filename) => {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const escape = (value) => {
    const str = String(value ?? '');
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const csv = [headers.join(','), ...rows.map((row) => headers.map((h) => escape(row[h])).join(','))].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const TAB_LABELS = { hosts: 'Hosts', packages: 'Packages', vulns: 'Vulnerabilities', versions: 'Versions', summary: 'Summary' };
const NOUNS = { hosts: ['host', 'hosts'], packages: ['package', 'packages'], vulns: ['vulnerability', 'vulnerabilities'], versions: ['version', 'versions'] };
const tabLabel = (tab) => TAB_LABELS[tab] || tab.charAt(0).toUpperCase() + tab.slice(1);
const resultCount = (n, type) => plural(n, ...(NOUNS[type] || ['result', 'results']));

const validRows = (value) => (ROW_SIZE_OPTIONS.includes(Number(value)) ? Number(value) : DEFAULT_ROWS);

// ── Result tables ───────────────────────────────────────────────────────────

const muted = (text) => <span className="text-graphite">{text}</span>;
const dash = (value) => (value === null || value === undefined || value === '' ? muted('—') : value);
const countCell = (n) => (n ? formatCount(n) : muted('0'));
const has = (rows, pick) => rows.some((r) => pick(r) !== undefined);

const versionStatus = (r) => {
  if (!r.outdated_host_count) return 'Latest';
  if (r.outdated_host_count === r.host_count) return muted('Outdated');
  return `${formatCount(r.host_count - r.outdated_host_count)} of ${plural(r.host_count, 'host')} latest`;
};

// Columns appear when any row on the page has the field, as on the legacy page.
function columnsFor(type, rows) {
  const vulnCount = (r) => r.active_vuln_count ?? r.vuln_count;
  const packageLink = (r) => (r.package_name ? <EntityLink kind="package" id={r.package_id} name={r.package_name} /> : muted('—'));
  const version = (key, header, pick) => ({ key, header, mono: true, maxWidth: 260, fullText: pick, render: (r) => dash(pick(r)) });

  if (type === 'hosts') {
    return [
      { key: 'host', header: 'Host', render: (r) => <EntityLink kind="host" id={r.host_id} name={r.hostname} /> },
      { key: 'region', header: 'Region', render: (r) => dash(environmentLabel(r.region)) },
      has(rows, (r) => r.package_name) && { key: 'package', header: 'Package', render: packageLink },
      has(rows, (r) => r.affected_packages) && {
        key: 'affected', header: 'Affected packages', mono: true, maxWidth: 320, fullText: (r) => r.affected_packages, render: (r) => dash(r.affected_packages),
      },
      has(rows, (r) => r.package_version) && version('version', 'Version', (r) => r.package_version),
      has(rows, (r) => r.fixed_version) && version('fixed', 'Fixed in', (r) => r.fixed_version),
      has(rows, vulnCount) && { key: 'vulns', header: 'Vulnerabilities', align: 'right', render: (r) => countCell(vulnCount(r)) },
      has(rows, (r) => r.affected_package_count) && !has(rows, (r) => r.affected_packages) && {
        key: 'packages', header: 'Packages', align: 'right', render: (r) => countCell(r.affected_package_count),
      },
    ].filter(Boolean);
  }
  if (type === 'packages') {
    return [
      { key: 'package', header: 'Package', render: packageLink },
      version('version', 'Version', (r) => r.package_version),
      has(rows, (r) => r.fixed_version) && version('fixed', 'Fixed in', (r) => r.fixed_version),
      has(rows, (r) => r.host_count) && { key: 'hosts', header: 'Hosts', align: 'right', render: (r) => countCell(r.host_count) },
      { key: 'vulns', header: 'Vulnerabilities', align: 'right', render: (r) => countCell(vulnCount(r)) },
      has(rows, (r) => r.is_latest) && { key: 'latest', header: 'Version status', render: (r) => (r.is_latest ? 'Latest' : muted('Outdated')) },
    ].filter(Boolean);
  }
  if (type === 'versions') {
    return [
      { key: 'package', header: 'Package', render: packageLink },
      version('version', 'Version', (r) => r.version),
      { key: 'hosts', header: 'Hosts', align: 'right', render: (r) => countCell(r.host_count) },
      { key: 'vulns', header: 'Vulnerabilities', align: 'right', render: (r) => countCell(r.vuln_count) },
      { key: 'status', header: 'Version status', render: versionStatus },
    ];
  }
  if (type === 'vulns') {
    const hosts = (r) => r.affected_host_count ?? r.affected_hosts;
    return [
      { key: 'cve', header: 'CVE', render: (r) => <EntityLink kind="cve" cve={r.cve} /> },
      { key: 'severity', header: 'Severity', render: (r) => <SeverityBadge severity={r.severity} /> },
      has(rows, (r) => r.package_name) && { key: 'package', header: 'Package', render: packageLink },
      has(rows, (r) => r.packages) && { key: 'packages', header: 'Packages', mono: true, maxWidth: 320, fullText: (r) => r.packages, render: (r) => dash(r.packages) },
      has(rows, hosts) && { key: 'hosts', header: 'Hosts', align: 'right', render: (r) => countCell(hosts(r)) },
      rows.some((r) => typeof r.affected_packages === 'number') && {
        key: 'affected', header: 'Affected packages', align: 'right', render: (r) => countCell(r.affected_packages),
      },
      has(rows, (r) => r.fixed_version) && version('fixed', 'Fixed in', (r) => r.fixed_version),
    ].filter(Boolean);
  }
  return [];
}

// ── Summary ─────────────────────────────────────────────────────────────────

function summaryFacts(summary) {
  const items = [];
  const add = (label, value, extra = {}) => value !== undefined && value !== null && items.push({ label, value, ...extra });
  if (summary.package) add('Package', <EntityLink kind="package" name={summary.package} />);
  if (summary.cve) add('CVE', <EntityLink kind="cve" cve={summary.cve} />);
  if (summary.hostname) add('Host', <EntityLink kind="host" name={summary.hostname} />);
  if (summary.severity) add('Severity', <SeverityBadge severity={summary.severity} />);
  add('Hosts', summary.total_hosts !== undefined ? formatCount(summary.total_hosts) : undefined);
  add('Packages', summary.total_packages !== undefined ? formatCount(summary.total_packages) : undefined);
  add('Versions', summary.total_versions !== undefined ? formatCount(summary.total_versions) : undefined);
  add('Unique CVEs', summary.unique_cves !== undefined ? formatCount(summary.unique_cves) : undefined);
  add('Findings', summary.total_findings !== undefined ? formatCount(summary.total_findings) : undefined);
  if (summary.unique_cves === undefined) add('Vulnerabilities', summary.total_vulns !== undefined ? formatCount(summary.total_vulns) : undefined);
  if (summary.critical_vulns > 0) add('Critical', <span className="text-crit">{formatCount(summary.critical_vulns)}</span>);
  if (summary.high_vulns > 0) add('High', <span className="text-high">{formatCount(summary.high_vulns)}</span>);
  return items;
}

function Summary({ summary, warnings }) {
  const facts = summary ? summaryFacts(summary) : [];
  const regions = summary?.regions || [];
  if (facts.length === 0 && regions.length === 0 && warnings.length === 0) return null;
  return (
    <div className="shrink-0 mb-4 space-y-3">
      {warnings.length > 0 && (
        <div role="status" className="flex items-start gap-2.5 px-3 py-2 rounded-panel border border-med/40 bg-med-tint text-body text-ink">
          <TriangleAlert size={16} strokeWidth={2} className="shrink-0 mt-0.5 text-med" aria-hidden="true" />
          <div>
            <p>The query ran, but part of it was ignored:</p>
            <ul className="mt-0.5 list-disc pl-5">
              {warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          </div>
        </div>
      )}
      {facts.length > 0 && <Facts items={facts} />}
      {regions.length > 0 && (
        <div className="flex flex-wrap items-baseline gap-x-7 gap-y-2">
          <span className="text-small text-graphite">Hosts by region</span>
          {regions.map((r) => (
            <span key={r.region} className="text-body">
              <span className="text-graphite">{environmentLabel(r.region)}</span>{' '}
              <span className="font-semibold tabular-nums">{formatCount(r.host_count)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Start state and syntax reference ────────────────────────────────────────

function SyntaxReference() {
  return (
    <section aria-label="Query syntax reference" className="shrink-0 mt-3 p-4 bg-sheet border border-rule rounded-panel">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-x-6 gap-y-4">
        {SYNTAX_GROUPS.map((group) => (
          <div key={group.title}>
            <h3 className="mb-1 text-small font-semibold text-graphite">{group.title}</h3>
            <ul className="m-0 p-0 list-none font-mono text-small text-ink leading-6">
              {group.tokens.map((t) => <li key={t}>{t}</li>)}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t border-rule grid grid-cols-2 gap-8">
        <div>
          <h3 className="mb-1 text-small font-semibold text-graphite">Filters each query reads</h3>
          <dl className="m-0 text-small">
            {FILTERS_BY_QUERY.map((row) => (
              <div key={row.title} className="flex gap-3 py-1">
                <dt className="w-36 shrink-0 text-ink">{row.title}</dt>
                <dd className="m-0 font-mono text-graphite">{row.filters}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div>
          <h3 className="mb-1 text-small font-semibold text-graphite">Operators</h3>
          <dl className="m-0 text-small">
            {TIPS.map((tip) => (
              <div key={tip.token} className="flex gap-3 py-1">
                <dt className="w-16 shrink-0 font-mono text-ink">{tip.token}</dt>
                <dd className="m-0 text-graphite">{tip.text}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}

const listButton = 'block w-full text-left px-2 -mx-2 py-2 rounded-control hover:bg-hover';

function StartState({ history, onRun, onClearHistory }) {
  return (
    <div className={`grid gap-12 mt-6 ${history.length ? 'grid-cols-[minmax(0,5fr)_minmax(0,4fr)]' : 'grid-cols-[minmax(0,640px)]'}`}>
      <section aria-labelledby="query-examples">
        <h2 id="query-examples" className="mb-1 text-section text-ink">Examples</h2>
        <ul className="m-0 p-0 list-none">
          {EXAMPLES.map((ex) => (
            <li key={ex.query} className="border-b border-rule last:border-b-0">
              <button type="button" className={listButton} onClick={() => onRun(ex.query)}>
                <span className="block font-mono text-id text-signal">{ex.query}</span>
                <span className="block mt-0.5 text-small text-graphite">{ex.text}</span>
              </button>
            </li>
          ))}
        </ul>
      </section>
      {history.length > 0 && (
        <section aria-labelledby="query-recent">
          <div className="flex items-baseline mb-1">
            <h2 id="query-recent" className="text-section text-ink">Recent queries</h2>
            <button type="button" onClick={onClearHistory} className="ml-auto text-small text-signal hover:underline">Clear recent</button>
          </div>
          <ul className="m-0 p-0 list-none">
            {history.slice(0, 10).map((q) => (
              <li key={q} className="border-b border-rule last:border-b-0">
                <button type="button" className={listButton} onClick={() => onRun(q)} title={q}>
                  <span className="block truncate font-mono text-id text-ink">{q}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

/**
 * URL keys as before: q (query), tab, page, rows. A new query adds a history entry; tab,
 * page and row changes replace it. Summary queries return no rows, so the first tab loads at once.
 */
export default function Investigation() {
  const location = useLocation();
  const routerHistory = useHistory();
  const inputRef = useRef(null);
  const written = useRef(null);
  const requestId = useRef(0);

  const [queryText, setQueryText] = useState('');
  const [run, setRun] = useState(null); // { text, summary, warnings, tabs, summaryMode }
  const [tab, setTab] = useState(null);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [result, setResult] = useState(null); // { result_type, data, total }
  const [tabTotals, setTabTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [history, setHistory] = useState(readHistory);

  const writeUrl = useCallback((next, { replace }) => {
    const params = new URLSearchParams();
    params.set('q', next.text);
    if (next.summaryMode && next.tab) params.set('tab', next.tab);
    if (next.page) params.set('page', String(next.page));
    if (next.rows !== DEFAULT_ROWS) params.set('rows', String(next.rows));
    const search = `?${params.toString()}`;
    written.current = search;
    routerHistory[replace ? 'replace' : 'push']({ pathname: location.pathname, search });
  }, [routerHistory, location.pathname]);

  const fetchRows = (text, { tab: override, page: p, rows: size }) => {
    const options = { offset: p * size, limit: size };
    if (override) options.result_type_override = override;
    return API.INVESTIGATE(text, options);
  };

  const execute = useCallback(async (text, { tab: wantedTab = null, page: wantedPage = 0, rows: size = DEFAULT_ROWS, push = true } = {}) => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    setRun(null);
    setResult(null);
    setTabTotals({});
    setRows(size);
    try {
      const base = await fetchRows(text, { page: wantedPage, rows: size });
      if (id !== requestId.current) return;
      const tabs = Array.isArray(base.tabs) ? base.tabs : [];
      const summaryMode = base.result_type === 'summary' || tabs.length > 1;
      let active = base.result_type;
      if (summaryMode) {
        if (wantedTab && tabs.includes(wantedTab)) active = wantedTab;
        else if (base.result_type === 'summary') active = tabs[0] || null;
      }
      let rowsResult = base;
      let p = wantedPage;
      if (active && (active !== base.result_type || !base.data)) {
        rowsResult = await fetchRows(text, { tab: active, page: p, rows: size });
        if (id !== requestId.current) return;
      } else if (!base.data) {
        p = 0;
      }
      setRun({ text, summary: base.summary, warnings: base.warnings || [], tabs, summaryMode });
      setTab(active);
      setPage(p);
      setResult(rowsResult.data ? rowsResult : null);
      if (rowsResult.data) setTabTotals({ [active]: rowsResult.total });
      setHistory(addToHistory(text));
      writeUrl({ text, tab: active, page: p, rows: size, summaryMode }, { replace: !push });
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err.message || 'The query could not run.');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [writeUrl]);

  // Loads a tab, page or page size of the current query.
  const reload = async (next) => {
    const merged = { tab, page, rows, ...next };
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    setTab(merged.tab);
    setPage(merged.page);
    setRows(merged.rows);
    if (next.tab !== undefined && next.tab !== tab) setResult(null);
    try {
      const response = await fetchRows(run.text, { tab: run.summaryMode ? merged.tab : null, page: merged.page, rows: merged.rows });
      if (id !== requestId.current) return;
      setResult(response);
      setTabTotals((totals) => ({ ...totals, [merged.tab]: response.total }));
      writeUrl({ text: run.text, ...merged, summaryMode: run.summaryMode }, { replace: true });
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err.message || 'The query could not run.');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  // Runs the query in the URL on arrival and on back/forward; skips URLs this page wrote itself.
  useEffect(() => {
    if (location.search === written.current) return;
    const params = new URLSearchParams(location.search);
    const q = (params.get('q') || '').trim();
    setQueryText(q);
    if (!q) {
      requestId.current += 1;
      written.current = location.search;
      setRun(null);
      setResult(null);
      setError(null);
      setLoading(false);
      inputRef.current?.focus();
      return;
    }
    execute(q, { tab: params.get('tab'), page: Number(params.get('page')) || 0, rows: validRows(params.get('rows')), push: false });
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (text) => {
    const q = text.trim();
    if (!q) return;
    setQueryText(q);
    execute(q, { rows });
  };

  const exportAll = async () => {
    if (!result?.total) return;
    setExporting(true);
    try {
      const batch = 500;
      let all = [];
      for (let offset = 0; offset < result.total; offset += batch) {
        const options = { offset, limit: batch };
        if (run.summaryMode && tab) options.result_type_override = tab;
        // eslint-disable-next-line no-await-in-loop
        const response = await API.INVESTIGATE(run.text, options);
        if (!response.data?.length) break;
        all = all.concat(response.data);
      }
      exportToCsv(all, `investigation_${result.result_type}_${result.total}_results_${Date.now()}.csv`);
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const type = result?.result_type || tab;
  const tableRows = result?.data || null;
  const columns = columnsFor(type, tableRows || []);
  const showTable = run && (tableRows || loading) && columns.length > 0;

  return (
    <div className="h-full flex flex-col overflow-y-auto px-6 pt-5 pb-5">
      <PageHeader
        title="Query"
        className="!mb-3"
        description={<>Find hosts, packages and vulnerabilities with filters, for example <span className="font-mono text-id text-ink">package:nginx version:&lt;1.31.0</span>.</>}
      />
      <form
        className="shrink-0 flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          submit(queryText);
        }}
      >
        <label htmlFor="query-input" className="sr-only">Query</label>
        <input
          id="query-input"
          ref={inputRef}
          type="text"
          value={queryText}
          onChange={(event) => setQueryText(event.target.value)}
          placeholder="type:hosts package:nginx version:<1.31.0"
          autoComplete="off"
          spellCheck={false}
          className="flex-1 min-w-0 h-10 px-3 rounded-control border border-rule bg-sheet font-mono text-id text-ink placeholder:text-graphite/70 outline-none focus:border-signal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
        />
        <Button type="submit" variant="primary" className="!h-10 !px-4" disabled={loading || !queryText.trim()}>
          {loading && !run ? 'Running…' : 'Run query'}
        </Button>
      </form>
      <div className="shrink-0 mt-2">
        <button
          type="button"
          aria-expanded={showHelp}
          onClick={() => setShowHelp((v) => !v)}
          className="inline-flex items-center gap-1 text-small text-signal hover:underline"
        >
          <ChevronRight size={14} className={`transition-transform ${showHelp ? 'rotate-90' : ''}`} aria-hidden="true" />
          Syntax reference
        </button>
      </div>
      {showHelp && <SyntaxReference />}

      {error && <ErrorState className="!px-0 !py-6" title="The query could not run" error={error} />}

      {!run && !loading && !error && (
        <div className="shrink-0">
          <StartState
            history={history}
            onRun={submit}
            onClearHistory={() => {
              localStorage.removeItem(HISTORY_KEY);
              setHistory([]);
            }}
          />
        </div>
      )}

      {(run || (loading && !error)) && (
        <div className="flex-1 min-h-[300px] flex flex-col mt-5">
          {!run && <Loading label="Running the query" className="!px-0" />}
          {run && <Summary summary={run.summary} warnings={run.warnings} />}
          {run && (
            <div className="shrink-0 flex items-end gap-3 mb-3 border-b border-rule">
              {run.summaryMode && run.tabs.length > 0 ? (
                <Tabs
                  className="!border-b-0"
                  label="Result type"
                  idPrefix="query"
                  tabs={run.tabs.map((t) => ({ id: t, label: tabLabel(t), count: tabTotals[t] }))}
                  value={tab}
                  onChange={(t) => t !== tab && reload({ tab: t, page: 0 })}
                />
              ) : (
                <p className="h-9 flex items-center text-body font-semibold text-ink">{result ? resultCount(result.total, type) : ' '}</p>
              )}
              {result?.total > 0 && (
                <Button size="sm" className="ml-auto mb-1.5" onClick={exportAll} disabled={exporting}>
                  <Download size={14} strokeWidth={1.75} className="text-graphite" aria-hidden="true" />
                  {exporting ? `Exporting ${formatCount(result.total)} rows…` : `Export all ${formatCount(result.total)}`}
                </Button>
              )}
            </div>
          )}
          {showTable ? (
            <DataTable
              className="flex-1"
              label={`Query results: ${tabLabel(type || 'results')}`}
              columns={columns}
              rows={tableRows}
              loading={loading}
              empty={(
                <EmptyState title={`No ${tabLabel(type).toLowerCase()} match this query`}>
                  Check the filter values against the syntax reference, or remove a filter to widen the search.
                </EmptyState>
              )}
              pagination={result && {
                total: result.total,
                page,
                rowsPerPage: rows,
                onPageChange: (p) => reload({ page: p }),
                onRowsPerPageChange: (size) => reload({ rows: size, page: 0 }),
              }}
            />
          ) : (
            run && !loading && (
              <EmptyState title="This query returned a summary only" className="!px-0">
                {run.tabs.length > 0 ? 'Choose a tab above to list its rows.' : 'Add type:hosts, type:packages or type:vulns to list rows.'}
              </EmptyState>
            )
          )}
        </div>
      )}
    </div>
  );
}
